/* eslint-disable no-empty */
import {
  Prospect,
  RequestBasePostProspect,
  RequestBasePostProspectGroupAssign,
  RequestBasePostProspectReply,
  ResponseBaseGetProspects,
  ResponseBasePostProspect,
  ResponseBasePostProspectGroupAssign,
  ResponseBasePostProspectReply,
  ResponseBasePostProspectSign,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

export const prospectsApi = createApi({
  reducerPath: 'prospectsApi',
  baseQuery: API.baseQuery,
  tagTypes: [],
  endpoints: (builder) => ({
    /**************************************************************************
     *                              ARTICLES
     *************************************************************************/
    fetchProspects: builder.query<ResponseBaseGetProspects, string | undefined>(
      {
        query: (request) =>
          `/prospects${request ? `?basketball-team-ids=${request}` : ''}`,
      }
    ),
    createProspect: builder.mutation<
      ResponseBasePostProspect,
      RequestBasePostProspect
    >({
      query: (request) => ({
        url: `/prospects`,
        method: 'POST',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const teamIds = data.basketball_teams?.map((team) => team.id) ?? [];
          teamIds.push(0);

          for (const teamId of teamIds) {
            dispatch(
              prospectsApi.util.updateQueryData(
                'fetchProspects',
                teamId === 0 ? '' : `${teamId}`,
                (draft) => {
                  draft.push(data);
                }
              )
            );
          }
        } catch (error: unknown) {}
      },
    }), // createProspect
    replyToProspect: builder.mutation<
      ResponseBasePostProspectReply,
      {
        prospectId: number;
        request: RequestBasePostProspectReply;
      }
    >({
      query: (request) => ({
        url: `/prospects/${request.prospectId}/reply`,
        method: 'POST',
        body: request.request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const teamIds = data.basketball_teams?.map((team) => team.id) ?? [];
          teamIds.push(0);

          for (const teamId of teamIds) {
            dispatch(
              prospectsApi.util.updateQueryData(
                'fetchProspects',
                teamId === 0 ? '' : `${teamId}`,
                (draft) =>
                  draft.map((prospect) =>
                    prospect.id === data.id ? data : prospect
                  )
              )
            );
          }
        } catch (error: unknown) {}
      },
    }), // replyToProspect
    assignGroups: builder.mutation<
      ResponseBasePostProspectGroupAssign,
      {
        prospectId: number;
        request: RequestBasePostProspectGroupAssign;
      }
    >({
      query: (request) => ({
        url: `/prospects/${request.prospectId}/groups`,
        method: 'POST',
        body: request.request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const teamIds = data.basketball_teams?.map((team) => team.id) ?? [];
          teamIds.push(0);

          for (const teamId of teamIds) {
            dispatch(
              prospectsApi.util.updateQueryData(
                'fetchProspects',
                teamId === 0 ? '' : `${teamId}`,
                (draft) =>
                  draft.map((prospect) =>
                    prospect.id === data.id ? data : prospect
                  )
              )
            );
          }
        } catch (error: unknown) {}
      },
    }), // assignGroups
    deleteProspect: builder.mutation<void, Prospect>({
      query: (prospect) => ({
        url: `/prospects/${prospect.id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(prospect, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const teamIds =
            prospect.basketball_teams?.map((team) => team.id) ?? [];
          teamIds.push(0);

          for (const teamId of teamIds) {
            dispatch(
              prospectsApi.util.updateQueryData(
                'fetchProspects',
                teamId === 0 ? '' : `${teamId}`,
                (draft) => draft.filter((p) => p.id !== prospect.id)
              )
            );
          }
        } catch (error: unknown) {}
      },
    }), // assignGroups
    signProspect: builder.mutation<ResponseBasePostProspectSign, number>({
      query: (id) => ({
        url: `/prospects/${id}/sign`,
        method: 'POST',
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const teamIds = data.basketball_teams?.map((team) => team.id) ?? [];
          teamIds.push(0);

          for (const teamId of teamIds) {
            dispatch(
              prospectsApi.util.updateQueryData(
                'fetchProspects',
                teamId === 0 ? '' : `${teamId}`,
                (draft) =>
                  draft.map((prospect) =>
                    prospect.id === data.id ? data : prospect
                  )
              )
            );
          }
        } catch (error: unknown) {}
      },
    }), // signProspect
  }), // end of endpoints
});

// Export hooks for usage in functional components
export const {
  useFetchProspectsQuery,
  useLazyFetchProspectsQuery,
  useCreateProspectMutation,
  useReplyToProspectMutation,
  useAssignGroupsMutation,
  useDeleteProspectMutation,
  useSignProspectMutation,
  reducerPath,
  reducer,
  middleware,
} = prospectsApi;
